import './table.scss';

import { range } from 'lodash';
import debounce from 'lodash.debounce';
import React, {
  forwardRef, useEffect, useImperativeHandle, useMemo,
} from 'react';
import {
  Button, ButtonGroup, Form, Table as BootstrapTable,
} from 'react-bootstrap';
import { BsArrowDownUp, BsSortDown, BsSortUp } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import {
  useFilters,
  useGlobalFilter,
  usePagination, useRowSelect, useSortBy, useTable,
} from 'react-table';

import { formatDate } from '../../../../services/format';
import { selectLoggedInUserPreferences } from '../../../../store/slices/auth-slice';

// A per-column search box rendered under a column header when `perColumnFiltering` is on.
// Keeps its own value for responsive typing and debounces the upward notification so a
// server-side grid doesn't refetch on every keystroke. Renders nothing for columns that
// cannot be filtered (e.g. edit/delete action columns without an accessor).
const ColumnFilterInput = ({ column, initialValue, onChanged }) => {
  const [value, setValue] = React.useState(initialValue ?? '');
  const onChangedRef = React.useRef(onChanged);
  onChangedRef.current = onChanged;

  const debounced = useMemo(
    () => debounce((columnId, v) => onChangedRef.current && onChangedRef.current(columnId, v), 300),
    [],
  );
  useEffect(() => () => debounced.cancel(), [debounced]);

  if (!column.canFilter) {
    return null;
  }

  return (
    <Form.Control
      size="sm"
      type="search"
      value={value}
      placeholder="Filter"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        const v = e.target.value;
        setValue(v);
        debounced(column.id, v);
      }}
    />
  );
};

// eslint-disable-next-line object-curly-newline
const PaginatedSelectTable = (forwardRef as any)(({
  columns,
  data,
  rowProps,
  initialState,
  setTableFilterUpdate,
  customTableClass,
  forcePaginationControl,
  manualGlobalFilter,
  manualPagination,
  manualSorting,
  onPaginationChange,
  onSortingChange,
  pageCount: pageCountProp,
  defaultPageSize,
  maxPageSize,
  onDataFiltered,
  noPagingPadding,
  perColumnFiltering,
  columnFilters,
  onColumnFilterChanged,
}, ref) => {
  const startState = { pageIndex: 0, pageSize: defaultPageSize || 25, ...initialState };
  const {
    datePreference,
    measurementPreference,
  } = useSelector(selectLoggedInUserPreferences);

  const dateFormatter = (dt) => {
    return formatDate(dt, datePreference);
  };

  const defaultColumn = useMemo(() => ({
    minWidth: 10,
    maxWidth: 100,
    width: 50,
    Footer: '',
    // Only when per-column filtering is on: give every column a case-insensitive
    // substring filter method so client-side filter boxes work (react-table skips a
    // column with no filter method). Scoped to perColumnFiltering so existing grids
    // (whose filter panel may drive boolean switches via setFilter) are unaffected.
    ...(perColumnFiltering ? { filter: 'text' } : {}),
  }), [perColumnFiltering]);

  const tablePropsObj = {
    columns,
    data,
    initialState: startState,
    autoResetFilters: false,
    defaultColumn,
    autoResetSortBy: !manualPagination,
    autoResetPage: !manualPagination,
    autoResetGlobalFilter: false,
    manualGlobalFilter,
    manualPagination,
    manualSortBy: manualSorting,
    onPaginationChange,
    onSortingChange,
  } as any;
  if (pageCountProp) {
    tablePropsObj.pageCount = pageCountProp;
  }
  const instance = useTable(
    tablePropsObj,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    prepareRow,
    rows,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page

    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = instance;

  useEffect(() => {
    if (onPaginationChange && manualPagination) {
      onPaginationChange({ pageIndex, pageSize });
    }
  }, [pageIndex, pageSize]);

  useEffect(() => {
    if (onSortingChange && manualSorting) {
      onSortingChange(sortBy);
    }
  }, [sortBy]);

  useImperativeHandle(ref, () => instance);

  // Render a totals footer only when at least one column defines a Footer.
  const showFooter = useMemo(
    () => (columns ?? []).some((c) => c.Footer !== undefined && c.Footer !== ''),
    [columns],
  );

  useEffect(() => {
    if (onDataFiltered) onDataFiltered(rows);
  }, [JSON.stringify(rows.map((r) => r.original))]);

  const getPageRange = (currentPage, numPages) => {
    if (numPages < 5) {
      return range(1, numPages + 1);
    }
    if (currentPage <= 3) {
      return range(1, 6);
    }
    if (currentPage + 3 > numPages) {
      return range(numPages - 4, numPages + 1);
    }
    return range(currentPage - 2, currentPage + 3);
  };

  const getTooltip = (column, row) => {
    if (!column.title) return undefined;
    if (typeof column.title === 'string') {
      return column.title;
    }
    return column.title(column, row, dateFormatter);
  };

  useEffect(() => {
    if (setTableFilterUpdate) {
      setTableFilterUpdate(rows.map((row) => row.original));
    }
  }, [setTableFilterUpdate, rows]);

  return (
    // apply the table props
    <div className="d-flex flex-column overflow-auto flex-grow-1 justify-content-between">
      <div className="flex-grow-1 overflow-auto">
        <BootstrapTable size="sm" striped hover {...getTableProps()} className={`custom-table ${customTableClass ?? ''}`}>
          <thead>
            {// Loop over the header rows
              headerGroups.map((headerGroup) => (
                // Apply the header row props
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {// Loop over the headers in each row
                    headerGroup.headers.map((column) => (
                      // Apply the header cell props
                      <th
                        {...column.getHeaderProps({
                          ...column.getSortByToggleProps(),
                          style: {
                            ...column.getSortByToggleProps()?.style,
                            width: column.fixedWidth,
                          },
                        })}
                      >
                        <div className="d-flex justify-content-between">
                          <div>
                            {// Render the header
                              column.render('Header')
                            }
                          </div>
                          <div>
                            {column.canSort && (
                              <span>
                                {
                                  /* eslint-disable */
                                  column.isSorted
                                    ? column.isSortedDesc
                                      ? <BsSortDown />
                                      : <BsSortUp />
                                    : <BsArrowDownUp color="gray" />}
                              </span>
                            )}
                          </div>
                        </div>
                      </th>
                    ))
                  }
                </tr>
              ))
            }
            {// Per-column filter row: one search box under each visible column header.
              perColumnFiltering && headerGroups.length > 0 && (
                <tr className="column-filter-row">
                  {headerGroups[headerGroups.length - 1].headers.map((column) => (
                    <th key={`column-filter-${column.id}`} style={{ width: column.fixedWidth }}>
                      <ColumnFilterInput
                        column={column}
                        initialValue={columnFilters ? columnFilters[column.id] : ''}
                        onChanged={onColumnFilterChanged}
                      />
                    </th>
                  ))}
                </tr>
              )
            }
          </thead>
          {/* Apply the table body props */}
          <tbody {...getTableBodyProps()}>
            {// Loop over the table rows
              page.map((row) => {
                // Prepare the row for display
                prepareRow(row);
                return (
                  // Apply the row props
                  <tr data-cy="table-row" {...row.getRowProps(rowProps(row))} >
                    {// Loop over the rows cells
                      row.cells.map((cell) => (
                        <td title={getTooltip(cell.column, row)} {...cell.getCellProps([
                          {
                            className: cell.column.className,
                            style: cell.column.style,
                          },
                        ])}
                        >
                          {// Render the cell contents
                            cell.render('Cell')
                          }
                        </td>
                      ))
                    }
                  </tr>
                );
              })
            }
          </tbody>
          {showFooter && (
            <tfoot>
              {footerGroups.map((group) => (
                <tr {...group.getFooterGroupProps()} className="fw-bold">
                  {group.headers.map((column) => (
                    <td
                      {...column.getFooterProps({
                        style: { width: column.fixedWidth },
                      })}
                    >
                      {column.render('Footer')}
                    </td>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </BootstrapTable>
      </div>
      {(data.length > 25 || forcePaginationControl || manualPagination) &&
        <div className={`w-100 bg-white bottom-0 pb-2 pt-2 ${noPagingPadding ? '' : 'pr-4'} ${noPagingPadding ? '' : 'pl-4'}`}>
          <div className="pagination d-flex align-items-center justify-content-between">

            <Form.Group className="pr-2">
              <Form.Select
                size="sm"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[25, 50, 75, maxPageSize ?? 100, data.length].map((size, i) => (
                  <option key={`${size}-${i}`} value={size} disabled={i == 4 && manualPagination}>
                    Show
                    {' '}
                    {i === 4 ? 'ALL' : size}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <ButtonGroup size="sm">
              <Button
                variant="outline-primary"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                {'<<'}
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {'<'}
              </Button>
              {getPageRange(pageIndex + 1, pageOptions.length).map((pg) => (
                <Button
                  key={`button-pg-${pg}`}
                  variant="outline-primary"
                  onClick={() => gotoPage(pg - 1)}
                  active={pageIndex + 1 === pg}
                >
                  {pg}
                </Button>
              ))}
              <Button
                variant="outline-primary"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                {'>'}
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
              >
                {'>>'}
              </Button>
            </ButtonGroup>
            <div>
              Page
              {' '}
              <strong>
                {pageIndex + 1}
                {' '}
                of
                {' '}
                {pageOptions.length}
              </strong>
              {' '}
            </div>
          </div>
        </div>
      }
    </div>
  );
});

export default PaginatedSelectTable;
